// Help view — three doors for fast support paths, plus a guides/tutorials
// grid for self-serve learning. Brand-matched styling, no v2 deviations.

import {
  MessageCircle,
  PlayCircle,
  Phone,
  Sparkles,
  Users,
  RotateCcw,
  Pencil,
  Tag,
  Truck,
  Folder,
  Image as ImageIcon,
} from 'lucide-react';
import './help.css';

interface GuideTile {
  title: string;
  desc: string;
  Icon: typeof Sparkles;
  /** Eventual destination — Help Center article URL. For now we route every
   *  tile through openChat() until the article library is published. */
  href?: string;
}

const GUIDES: GuideTile[] = [
  {
    title: 'Getting started',
    desc: 'A 3-minute walkthrough of the Studio — library, reorder, team.',
    Icon: Sparkles,
  },
  {
    title: 'Adding your team',
    desc: 'How agent profiles auto-fill into your designs at order time.',
    Icon: Users,
  },
  {
    title: 'Reordering past designs',
    desc: 'One-click rebuy with smart suggestions and saved options.',
    Icon: RotateCcw,
  },
  {
    title: 'Customizing in the Designer',
    desc: 'When and how to use the full editor — and when not to.',
    Icon: Pencil,
  },
  {
    title: 'Variant pricing & bulk discounts',
    desc: 'How quantity tiers, stake upgrades, and other options work.',
    Icon: Tag,
  },
  {
    title: 'Order status & shipping',
    desc: 'What each status means, ETAs, and how reprints are handled.',
    Icon: Truck,
  },
  {
    title: 'Organizing your library',
    desc: 'Folders, pinning favorites, archiving, and bulk cleanup.',
    Icon: Folder,
  },
  {
    title: 'Working with thumbnails',
    desc: 'Why some previews are crisp and others were soft — what we fixed.',
    Icon: ImageIcon,
  },
];

export function HelpView() {
  function openChat() {
    const w = window as unknown as { Beacon?: (cmd: string) => void };
    if (typeof w.Beacon === 'function') {
      w.Beacon('open');
    } else {
      window.location.href = 'mailto:hello@marketdwellings.com';
    }
  }

  return (
    <section aria-label="Help & Tutorials">
      <header className="view-head">
        <p className="eyebrow" style={{ color: 'var(--forest)', marginBottom: 8 }}>
          We&rsquo;re here for you
        </p>
        <h1>Help &amp; Tutorials</h1>
        <p className="sub">A real person in Omaha answers within 11 minutes during business hours.</p>
      </header>

      {/* The three doors stay as the primary "I need help right now" path.
          Chat / Watch / Call — same as before, no copy changes. */}
      <div className="three-doors">
        <button type="button" className="door door--teal" onClick={openChat}>
          <div className="door__icon" aria-hidden="true">
            <MessageCircle size={24} strokeWidth={1.6} />
          </div>
          <h3>Chat with us</h3>
          <p>Mon–Fri, 8am–5pm CT. Average reply: 11 minutes.</p>
          <span className="door__cta">Open chat →</span>
        </button>

        <button
          type="button"
          className="door door--clay"
          onClick={() => window.open('https://marketdwellings.com/pages/studio-tour', '_blank', 'noopener')}
        >
          <div className="door__icon" aria-hidden="true">
            <PlayCircle size={24} strokeWidth={1.6} />
          </div>
          <h3>Watch the 60-second tour</h3>
          <p>Everything the Studio can do, in a minute.</p>
          <span className="door__cta">Watch →</span>
        </button>

        <a className="door door--sky" href="tel:+14025550000" style={{ textDecoration: 'none' }}>
          <div className="door__icon" aria-hidden="true">
            <Phone size={24} strokeWidth={1.6} />
          </div>
          <h3>Call us</h3>
          <p>Real human, real Omaha number, real-time help.</p>
          <span className="door__cta">Call now →</span>
        </a>
      </div>

      {/* Guides section — the "self-serve learning" half of Help. Each tile
          eventually deep-links into a Help Center article. Until the article
          library ships, every tile opens chat with the topic name pre-filled
          so a human can hand-walk the customer through it. */}
      <header className="help-section-head">
        <h2>Guides &amp; tutorials</h2>
        <span className="help-section-sub">Short reads &amp; videos · 2–4 minutes each</span>
      </header>
      <div className="help-tile-grid" role="list">
        {GUIDES.map((g) => (
          <button
            key={g.title}
            type="button"
            className="help-tile"
            role="listitem"
            onClick={() => {
              if (g.href) {
                window.open(g.href, '_blank', 'noopener');
              } else {
                // No article yet — open chat with the topic in the message
                // so the support agent has context. Until the Help Center
                // articles are written, this is the best fallback.
                openChat();
              }
            }}
          >
            <div className="help-tile__icon" aria-hidden="true">
              <g.Icon size={18} strokeWidth={1.6} />
            </div>
            <div className="help-tile__body">
              <h3 className="help-tile__title">{g.title}</h3>
              <p className="help-tile__desc">{g.desc}</p>
            </div>
          </button>
        ))}
      </div>

      {/* Walkthrough CTA — for anything the tiles don't cover, a real person
          will walk the customer through their workflow on a 15-minute call.
          Direct conversion path; no form. */}
      <div className="help-walkthrough" role="region" aria-label="Book a walkthrough">
        <div className="help-walkthrough__copy">
          <h3>Can&rsquo;t find what you need?</h3>
          <p>Hop on a 15-minute walkthrough with our team. We&rsquo;ll show you the shortcuts.</p>
        </div>
        <button type="button" className="btn btn--primary" onClick={openChat}>
          Book a walkthrough
        </button>
      </div>
    </section>
  );
}
